import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Lock,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  Filter,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select, type SelectOption } from "@/components/ui/Select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { CaseNoteEditor } from "./CaseNoteEditor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CaseNote {
  id: number;
  person_id: number;
  title: string | null;
  note_text: string | null;
  note_type: string;
  note_date: string | null;
  note_time: string | null;
  is_locked: number;
  is_unalterable: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  author_first_name?: string;
  author_last_name?: string;
  reviewer_first_name?: string;
  reviewer_last_name?: string;
}

interface NotesResponse {
  data: CaseNote[];
  total: number;
  page: number;
  limit: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const noteTypeOptions: SelectOption[] = [
  { value: "", label: "All Types" },
  { value: "general", label: "General" },
  { value: "clinical", label: "Clinical" },
  { value: "legal", label: "Legal" },
  { value: "case_management", label: "Case Management" },
  { value: "counseling", label: "Counseling" },
  { value: "advocacy", label: "Advocacy" },
  { value: "other", label: "Other" },
];

const noteTypeBadgeColors: Record<string, string> = {
  general: "bg-gray-100 text-gray-800",
  clinical: "bg-purple-100 text-purple-800",
  legal: "bg-amber-100 text-amber-800",
  case_management: "bg-blue-100 text-blue-800",
  counseling: "bg-green-100 text-green-800",
  advocacy: "bg-teal-100 text-teal-800",
  other: "bg-slate-100 text-slate-800",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CaseNotesListProps {
  personId: string;
  autoOpenEditor?: boolean;
  onEditorOpened?: () => void;
}

export function CaseNotesList({ personId, autoOpenEditor, onEditorOpened }: CaseNotesListProps) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [noteType, setNoteType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showEditor, setShowEditor] = useState(false);
  const [editingNote, setEditingNote] = useState<CaseNote | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Auto-open editor when triggered from header button
  useEffect(() => {
    if (autoOpenEditor) {
      setShowEditor(true);
      setEditingNote(null);
      onEditorOpened?.();
    }
  }, [autoOpenEditor, onEditorOpened]);

  const params: Record<string, string> = { page: String(page), limit: "25" };
  if (noteType) params.noteType = noteType;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const { data, isLoading } = useQuery<NotesResponse>({
    queryKey: ["case-notes", personId, page, noteType, startDate, endDate],
    queryFn: () =>
      api.get<NotesResponse>(`/clients/${personId}/notes`, { params }),
    enabled: !!personId,
  });

  const lockMutation = useMutation({
    mutationFn: (noteId: number) =>
      api.post(`/clients/${personId}/notes/${noteId}/lock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
    },
  });

  const reviewMutation = useMutation({
    mutationFn: (noteId: number) =>
      api.post(`/clients/${personId}/notes/${noteId}/review`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: number) =>
      api.delete(`/clients/${personId}/notes/${noteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-notes", personId] });
    },
  });

  function toggleExpanded(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleEdit(note: CaseNote) {
    setEditingNote(note);
    setShowEditor(true);
  }

  function handleEditorClose() {
    setShowEditor(false);
    setEditingNote(null);
  }

  if (showEditor) {
    return (
      <CaseNoteEditor
        personId={personId}
        note={editingNote}
        onClose={handleEditorClose}
      />
    );
  }

  const notes = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Case Notes</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setShowEditor(true)}
          >
            <Plus className="h-4 w-4" />
            New Note
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="w-48">
                <Select
                  label="Note Type"
                  options={noteTypeOptions}
                  value={noteType}
                  onChange={setNoteType}
                  clearable
                />
              </div>
              <div className="w-48">
                <Input
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="w-48">
                <Input
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && notes.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No case notes found.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Notes list */}
      {!isLoading && notes.length > 0 && (
        <div className="space-y-3">
          {notes.map((note) => {
            const isExpanded = expandedIds.has(note.id);
            const authorName =
              note.author_first_name && note.author_last_name
                ? `${note.author_first_name} ${note.author_last_name}`
                : "Unknown";
            const reviewerName =
              note.reviewer_first_name && note.reviewer_last_name
                ? `${note.reviewer_first_name} ${note.reviewer_last_name}`
                : null;

            return (
              <Card key={note.id}>
                <CardHeader
                  className="cursor-pointer pb-2"
                  onClick={() => toggleExpanded(note.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <CardTitle className="text-sm font-medium">
                          {note.title || "Untitled Note"}
                        </CardTitle>
                        <CardDescription className="mt-0.5">
                          {note.note_date
                            ? new Date(note.note_date).toLocaleDateString()
                            : "No date"}{" "}
                          &middot; {authorName}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Type badge */}
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                          noteTypeBadgeColors[note.note_type] ??
                            noteTypeBadgeColors.other,
                        )}
                      >
                        {note.note_type.replace("_", " ")}
                      </span>

                      {/* Lock indicator */}
                      {note.is_locked === 1 && (
                        <span title="Locked"><Lock className="h-4 w-4 text-amber-500" /></span>
                      )}

                      {/* Review indicator */}
                      {note.reviewed_by && (
                        <span title={`Reviewed by ${reviewerName ?? "supervisor"}`}>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent>
                    <div className="space-y-3">
                      <p className="whitespace-pre-wrap text-sm text-foreground">
                        {note.note_text || "No content."}
                      </p>

                      {/* Review status */}
                      {reviewerName && note.reviewed_at && (
                        <p className="text-xs text-muted-foreground">
                          Reviewed by {reviewerName} on{" "}
                          {new Date(note.reviewed_at).toLocaleDateString()}
                        </p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 border-t pt-3">
                        {note.is_locked === 0 && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(note);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                lockMutation.mutate(note.id);
                              }}
                              loading={lockMutation.isPending}
                            >
                              <Lock className="mr-1 h-3 w-3" />
                              Lock
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  confirm(
                                    "Are you sure you want to delete this note?",
                                  )
                                ) {
                                  deleteMutation.mutate(note.id);
                                }
                              }}
                              loading={deleteMutation.isPending}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        {!note.reviewed_by && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              reviewMutation.mutate(note.id);
                            }}
                            loading={reviewMutation.isPending}
                          >
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Mark Reviewed
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {notes.length} of {total} notes
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
